import {
  createPromoCodeMutation,
  deletePromoCodeMutation,
  sendPromoCodeMutation,
  updatePromoCodeMutation,
} from '@/api/promo-codes/promoMutations';
import type { PromoCode } from '@/api/promo-codes/promoQueries';
import { getPromoCodesQuery } from '@/api/promo-codes/promoQueries';
import { ConfirmDeleteDialog } from '@/components/common/ConfirmDeleteDialog';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { TableCell, TableHead } from '@/components/common/Table';
import { useFormMutation } from '@/hooks/useFormMutation';
import { promoCodeSchema } from '@/validation/promoValidator';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { useState } from 'react';
import { toast } from 'sonner';
import { toFormikValidate } from 'zod-formik-adapter';
import { DashboardTableAccordion } from '../../DashboardTableAccordion';
import { EditRecipientEmailDialog } from './EditRecipientEmailDialog';
import { GeneratePromoCodeForm } from './GeneratePromoCodeForm';

export const DiscountRow = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPromoCode, setSelectedPromoCode] = useState<PromoCode | null>(
    null,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [codeToDelete, setCodeToDelete] = useState<string | null>(null);
  const { data: response, isLoading } = useQuery(getPromoCodesQuery(page));
  const promoCodes = response?.data || [];
  const meta = response?.meta;

  const generateFormik = useFormik({
    initialValues: { discount: '' as unknown as number },
    validate: toFormikValidate(promoCodeSchema),
    onSubmit: async (values) => {
      await generateCode.mutateAsync(values);
      generateFormik.resetForm();
    },
  });

  const generateCode = useFormMutation({
    ...createPromoCodeMutation(),
    formik: generateFormik,
  });

  const deleteCode = useFormMutation(deletePromoCodeMutation());

  const updateMutation = useMutation({
    ...updatePromoCodeMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promo-codes'] });
      setDialogOpen(false);
      setSelectedPromoCode(null);
      toast.success('Recipient assigned successfully');
    },
  });

  const sendMutation = useMutation({
    ...sendPromoCodeMutation(),
    onSuccess: () => {
      setDialogOpen(false);
      setSelectedPromoCode(null);
      toast.success('Promo code sent successfully');
    },
  });

  const handleDeleteClick = (code: string) => {
    setCodeToDelete(code);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (codeToDelete) {
      await deleteCode.mutateAsync(codeToDelete);
      setDeleteDialogOpen(false);
      setCodeToDelete(null);
    }
  };

  const handleEdit = (promoCode: PromoCode) => {
    setSelectedPromoCode(promoCode);
    setDialogOpen(true);
  };

  const handleUpdateEmail = (code: string, email: string) => {
    updateMutation.mutate({
      promo_code: code,
      email: email,
    });
  };

  const handleSendEmail = (code: string, email: string) => {
    sendMutation.mutate({ promo_code: code, email: email });
  };

  return (
    <>
      <DashboardTableAccordion
        value="discounts"
        title="Discounts"
        data={promoCodes.map((promo) => ({ ...promo, id: promo.code }))}
        isLoading={isLoading}
        loadingMessage="Loading promo codes..."
        paginationMeta={meta}
        onPageChange={setPage}
        summarySection={
          <GeneratePromoCodeForm
            formik={generateFormik}
            isPending={generateCode.isPending}
          />
        }
        renderHeader={() => (
          <>
            <TableHead>Code</TableHead>
            <TableHead>Discount</TableHead>
            <TableHead>Order ID</TableHead>
            <TableHead>Valid till</TableHead>
            <TableHead className="w-30"></TableHead>
          </>
        )}
        renderRow={(item) => (
          <>
            <TableCell>{item.code}</TableCell>
            <TableCell>{item.discount}%</TableCell>
            <TableCell>{item.order_id ?? '-'}</TableCell>
            <TableCell>{item.expire_date}</TableCell>
            <TableCell>
              <div className="flex gap-4">
                <RelationActionButton
                  onClick={() => handleEdit(item)}
                  disabled={deleteCode.isPending || item.is_used}
                >
                  Assign recipient
                </RelationActionButton>
                <RelationActionButton
                  onClick={() => handleDeleteClick(item.code)}
                  disabled={deleteCode.isPending}
                  className="text-delete"
                >
                  Delete
                </RelationActionButton>
              </div>
            </TableCell>
          </>
        )}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete promo code"
        description="Are you sure you want to delete this promo code?"
        onConfirm={handleDeleteConfirm}
        isPending={deleteCode.isPending}
      />

      <EditRecipientEmailDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        promoCode={selectedPromoCode}
        onSubmit={handleUpdateEmail}
        onSend={handleSendEmail}
        isSendPending={sendMutation.isPending}
      />
    </>
  );
};
